// ══════════════════════════════════════════════════
// ARIA Persistent Memory System
// Survives across sessions — remembers people, topics,
// preferences, and conversation patterns
// ══════════════════════════════════════════════════

const MEMORY_KEY = 'aria_memory';
const MEMORY_VERSION = 1;

// ── Load/Save ──
function loadMemory() {
  try {
    const raw = localStorage.getItem(MEMORY_KEY);
    if (!raw) return createFreshMemory();
    const mem = JSON.parse(raw);
    if (mem.version !== MEMORY_VERSION) return createFreshMemory();
    return mem;
  } catch (e) {
    console.warn('Failed to load ARIA memory:', e);
    return createFreshMemory();
  }
}

function saveMemory(mem) {
  try {
    mem.lastUpdated = Date.now();
    localStorage.setItem(MEMORY_KEY, JSON.stringify(mem));
  } catch (e) {
    console.warn('Failed to save ARIA memory:', e);
  }
}

function createFreshMemory() {
  return {
    version: MEMORY_VERSION,
    lastUpdated: Date.now(),
    // People ARIA has learned about
    entities: {},
    // Topics discussed with frequency + recency
    topics: {},
    // User preferences ARIA has inferred
    preferences: {},
    // Conversation summaries (last 20 sessions)
    sessions: [],
    // Key facts ARIA should remember
    facts: [],
    // Interaction patterns
    patterns: {
      avgMessageLength: 0,
      totalMessages: 0,
      preferredTime: null,
      commonIntents: {},
    },
  };
}

// ── Entity Extraction ──
// Pull out names, companies, projects from text
function extractEntities(text) {
  const entities = [];

  // Names — capitalized two-word patterns
  const namePattern = /\b([A-Z][a-z]+)\s+([A-Z][a-z]+)\b/g;
  let match;
  while ((match = namePattern.exec(text)) !== null) {
    const name = `${match[1]} ${match[2]}`;
    // Skip common false positives
    if (!/^(Good Morning|This Is|Let Me|Want Me|I Would|The Next|How Would|What If|Thank You|No Problem|Of Course)$/i.test(name)) {
      entities.push({ type: 'person', name });
    }
  }

  // Email addresses
  const emailPattern = /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g;
  while ((match = emailPattern.exec(text)) !== null) {
    entities.push({ type: 'email', value: match[1] });
  }

  // Companies — look for common suffixes or known patterns
  const companyPattern = /\b([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)*)\s+(?:Inc|LLC|Corp|Fund|Capital|Ventures|Partners|Group|AI|Tech)\b/g;
  while ((match = companyPattern.exec(text)) !== null) {
    entities.push({ type: 'company', name: match[0] });
  }

  return entities;
}

// ── Topic Extraction ──
function extractTopics(text) {
  const lower = text.toLowerCase();
  const topics = [];

  const topicPatterns = {
    'investor_relations': /investor|funding|pitch|raise|capital|vc|angel|seed|series/,
    'sales': /deal|pipeline|prospect|lead|close|proposal|contract|client/,
    'product': /feature|build|ship|deploy|release|bug|sprint|roadmap/,
    'meetings': /meeting|call|sync|standup|review|agenda|prep/,
    'email': /email|reply|follow.?up|thread|draft|send|inbox/,
    'strategy': /strategy|plan|vision|goals|okr|kpi|growth|market/,
    'operations': /team|hire|budget|burn|revenue|mrr|arr|runway/,
    'partnerships': /partner|collaborate|integrate|alliance|joint/,
    'content': /blog|post|article|linkedin|twitter|content|write/,
  };

  for (const [topic, pattern] of Object.entries(topicPatterns)) {
    if (pattern.test(lower)) topics.push(topic);
  }

  return topics;
}

// ══════════════════════════════════════════════════
// PUBLIC API
// ══════════════════════════════════════════════════

// Record a conversation exchange
export function rememberConversation(userMessage, ariaReply) {
  const mem = loadMemory();

  // Extract and store entities
  const allText = userMessage + ' ' + ariaReply;
  const entities = extractEntities(allText);
  for (const entity of entities) {
    const key = entity.name || entity.value;
    if (!key) continue;
    if (!mem.entities[key]) {
      mem.entities[key] = {
        type: entity.type,
        firstSeen: Date.now(),
        lastSeen: Date.now(),
        mentions: 1,
        contexts: [],
      };
    } else {
      mem.entities[key].lastSeen = Date.now();
      mem.entities[key].mentions++;
    }
    // Store snippet of context (last 3)
    const snippet = userMessage.slice(0, 120);
    mem.entities[key].contexts = [snippet, ...(mem.entities[key].contexts || [])].slice(0, 3);
  }

  // Extract and store topics
  const topics = extractTopics(allText);
  for (const topic of topics) {
    if (!mem.topics[topic]) {
      mem.topics[topic] = { count: 1, lastDiscussed: Date.now(), firstDiscussed: Date.now() };
    } else {
      mem.topics[topic].count++;
      mem.topics[topic].lastDiscussed = Date.now();
    }
  }

  // Update interaction patterns
  mem.patterns.totalMessages++;
  mem.patterns.avgMessageLength = Math.round(
    (mem.patterns.avgMessageLength * (mem.patterns.totalMessages - 1) + userMessage.length) / mem.patterns.totalMessages
  );
  const hour = new Date().getHours();
  mem.patterns.preferredTime = hour;

  saveMemory(mem);
}

// Record a full session summary
export function rememberSession(messages) {
  const mem = loadMemory();

  // Build a brief summary
  const userMsgs = messages.filter(m => m.type === 'user').map(m => m.text.replace(/<[^>]+>/g, ''));

  if (userMsgs.length === 0) return;

  const session = {
    date: Date.now(),
    dateStr: new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    messageCount: messages.length,
    topics: [...new Set(userMsgs.flatMap(m => extractTopics(m)))],
    entities: [...new Set(userMsgs.flatMap(m => extractEntities(m)).map(e => e.name || e.value).filter(Boolean))],
    keyQuestions: userMsgs.filter(m => m.includes('?')).slice(0, 3).map(m => m.slice(0, 100)),
    // First and last user messages as session bookends
    opener: userMsgs[0]?.slice(0, 80) || '',
    closer: userMsgs[userMsgs.length - 1]?.slice(0, 80) || '',
  };

  mem.sessions = [session, ...mem.sessions].slice(0, 20);
  saveMemory(mem);
}

// Store a specific fact ARIA learned
export function rememberFact(fact, source) {
  const mem = loadMemory();
  mem.facts = [
    { text: fact, source, date: Date.now() },
    ...mem.facts
  ].slice(0, 50);
  saveMemory(mem);
}

// Store a user preference ARIA inferred
export function rememberPreference(key, value) {
  const mem = loadMemory();
  mem.preferences[key] = { value, updatedAt: Date.now() };
  saveMemory(mem);
}

// ══════════════════════════════════════════════════
// RECALL — Build context for ARIA prompts
// ══════════════════════════════════════════════════

// Get memory context to inject into ARIA's system prompt
export function getMemoryContext() {
  const mem = loadMemory();
  const parts = [];

  // Recent session summaries
  if (mem.sessions.length > 0) {
    parts.push('PREVIOUS CONVERSATIONS:');
    mem.sessions.slice(0, 5).forEach(s => {
      const topicsStr = s.topics.length > 0 ? ` [${s.topics.join(', ')}]` : '';
      const entitiesStr = s.entities.length > 0 ? ` — mentioned: ${s.entities.join(', ')}` : '';
      parts.push(`• ${s.dateStr}: ${s.messageCount} messages${topicsStr}${entitiesStr}`);
      if (s.keyQuestions.length > 0) {
        parts.push(`  Asked: "${s.keyQuestions[0]}"`);
      }
    });
  }

  // Key people
  const sortedEntities = Object.entries(mem.entities)
    .filter(([_, e]) => e.type === 'person' && e.mentions >= 2)
    .sort((a, b) => b[1].mentions - a[1].mentions)
    .slice(0, 10);

  if (sortedEntities.length > 0) {
    parts.push('\nKEY PEOPLE (from past conversations):');
    sortedEntities.forEach(([name, e]) => {
      const daysAgo = Math.floor((Date.now() - e.lastSeen) / (1000 * 60 * 60 * 24));
      const recency = daysAgo === 0 ? 'today' : daysAgo === 1 ? 'yesterday' : `${daysAgo}d ago`;
      parts.push(`• ${name}: mentioned ${e.mentions}x, last: ${recency}`);
    });
  }

  // Top topics
  const sortedTopics = Object.entries(mem.topics)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 6);

  if (sortedTopics.length > 0) {
    parts.push('\nFREQUENT TOPICS:');
    sortedTopics.forEach(([topic, t]) => {
      const daysAgo = Math.floor((Date.now() - t.lastDiscussed) / (1000 * 60 * 60 * 24));
      const recency = daysAgo === 0 ? 'today' : `${daysAgo}d ago`;
      parts.push(`• ${topic.replace(/_/g, ' ')}: ${t.count} discussions, last: ${recency}`);
    });
  }

  // Stored facts
  if (mem.facts.length > 0) {
    parts.push('\nKEY FACTS:');
    mem.facts.slice(0, 8).forEach(f => {
      parts.push(`• ${f.text}`);
    });
  }

  // Preferences
  const prefs = Object.entries(mem.preferences);
  if (prefs.length > 0) {
    parts.push('\nUSER PREFERENCES:');
    prefs.forEach(([key, p]) => {
      parts.push(`• ${key}: ${p.value}`);
    });
  }

  return parts.join('\n');
}

// Get a quick summary for the greeting
export function getMemorySummary() {
  const mem = loadMemory();
  return {
    totalSessions: mem.sessions.length,
    totalMessages: mem.patterns.totalMessages,
    topPeople: Object.entries(mem.entities)
      .filter(([_, e]) => e.type === 'person')
      .sort((a, b) => b[1].mentions - a[1].mentions)
      .slice(0, 5)
      .map(([name]) => name),
    topTopics: Object.entries(mem.topics)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([topic]) => topic.replace(/_/g, ' ')),
    lastSession: mem.sessions[0] || null,
    recentFacts: mem.facts.slice(0, 3).map(f => f.text),
  };
}

// Clear all memory
export function clearMemory() {
  localStorage.removeItem(MEMORY_KEY);
}
